import React, { useState, useEffect } from "react";
import {
  X,
  Calendar,
  Clock,
  MapPin,
  Flag,
  Repeat,
  Paperclip,
  File,
} from "lucide-react";
import useCalendar from "../../hooks/useCalendar";

/* ===== 유틸: 날짜/시간을 인풋 포맷으로 정규화 ===== */
// 로컬 YYYY-MM-DD
const toDateInput = (v) => {
  if (!v) return "";
  if (v instanceof Date) {
    const y = v.getFullYear();
    const m = String(v.getMonth() + 1).padStart(2, "0");
    const d = String(v.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }
  return String(v).slice(0, 10);
};

// HH:MM
const toTimeInput = (v) => {
  if (!v) return "";
  const s = String(v);
  const timePart = s.includes("T") ? s.split("T")[1] : s;
  return timePart.slice(0, 5);
};

/**
 * 일정 생성/수정 모달
 */
export default function TaskModal({
  isOpen,
  onClose,
  onSave,
  task = null,
  calendarId,
  onFileSelect,
  selectedFiles = [],
  initialDate = null,
}) {
  const { fetchMyCalendars } = useCalendar();
  const [calendars, setCalendars] = useState([]);
  const [formData, setFormData] = useState({
    calendar_id: calendarId || "",
    title: "",
    description: "",
    task_date: "",
    start_time: "",
    end_time: "",
    is_all_day: false,
    category: "work",
    priority: "medium",
    location: "",
    reminder: 15,
    repeat_config: null,
    attendee_files: [],
  });
  const [errors, setErrors] = useState({});
  const [showRepeatConfig, setShowRepeatConfig] = useState(false);

  useEffect(() => {
    if (isOpen) {
      (async () => {
        const result = await fetchMyCalendars();
        if (result.ok) {
          setCalendars(result.calendars);
          if (!formData.calendar_id && result.calendars.length > 0) {
            setFormData((prev) => ({
              ...prev,
              calendar_id: result.calendars[0].id,
            }));
          }
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // ✨ 핵심 수정: task가 있을 때 event_date를 task_date로 매핑, 시간 HH:MM로 정규화
  useEffect(() => {
    if (task) {
      setFormData((prev) => ({
        ...prev,
        calendar_id: calendarId || task.calendar_id || prev.calendar_id || "",
        title: task.title || "",
        description: task.description || "",
        task_date: toDateInput(task.task_date || task.event_date), // ← 여기!
        start_time: toTimeInput(task.start_time),
        end_time: toTimeInput(task.end_time),
        is_all_day: !!task.is_all_day,
        category: task.category || "work",
        priority: task.priority || "medium",
        location: task.location || "",
        reminder: task.reminder ?? 15,
        // 반복 정보가 있으면 폼에 넣어두기(수정에서 보여주기만)
        repeat_config: task.recurring_info
          ? {
              repeat_type: task.recurring_info.repeat_type,
              repeat_interval: task.recurring_info.repeat_interval,
              repeat_days: task.recurring_info.repeat_days || [],
              start_date: toDateInput(task.recurring_info.repeat_start_date),
              end_date: task.recurring_info.repeat_end_date
                ? toDateInput(task.recurring_info.repeat_end_date)
                : undefined,
            }
          : null,
      }));
      setShowRepeatConfig(!!task.recurring_info);
    } else {
      const base = initialDate ? new Date(initialDate) : new Date(); // 로컬 기준
      setFormData((prev) => ({
        ...prev,
        calendar_id: calendarId || prev.calendar_id || "",
        title: "",
        description: "",
        task_date: toDateInput(base), // ← UTC ISO 금지
        start_time: "",
        end_time: "",
        is_all_day: false,
        category: "work",
        priority: "medium",
        location: "",
        reminder: 15,
        repeat_config: null,
        attendee_files: [],
      }));
      setShowRepeatConfig(false);
    }
    setErrors({});
  }, [task, calendarId, isOpen, initialDate]);

  const handleSubmit = (e) => {
    e.preventDefault();

    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = "제목을 입력해주세요.";
    if (!formData.task_date && !showRepeatConfig)
      newErrors.task_date = "날짜를 선택해주세요.";
    if (!formData.is_all_day && formData.start_time && formData.end_time) {
      if (formData.start_time >= formData.end_time) {
        newErrors.time = "종료 시간은 시작 시간보다 늦어야 합니다.";
      }
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const submitData = { ...formData };
    if (showRepeatConfig) {
      submitData.repeat_config = {
        repeat_type: formData.repeat_type || "daily",
        repeat_interval: formData.repeat_interval || 1,
        repeat_days: formData.repeat_days || [],
        start_date: formData.repeat_start_date || formData.task_date,
        end_date: formData.repeat_end_date,
      };
    } else {
      submitData.repeat_config = null;
    }

    onSave(submitData);
  };

  const categories = [
    { value: "work", label: "업무" },
    { value: "meeting", label: "회의" },
    { value: "deadline", label: "마감일" },
    { value: "personal", label: "개인" },
    { value: "other", label: "기타" },
  ];

  const priorities = [
    { value: "low", label: "낮음", color: "text-gray-500" },
    { value: "medium", label: "보통", color: "text-yellow-500" },
    { value: "high", label: "높음", color: "text-red-500" },
  ];

  if (!isOpen) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold">
              {task ? "일정 수정" : "새 일정 만들기"}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            {/* 캘린더 선택 */}
            <div className="mb-4">
              <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                <Calendar className="w-4 h-4 mr-1" />
                캘린더 선택
              </label>
              <select
                value={formData.calendar_id}
                onChange={(e) =>
                  setFormData({ ...formData, calendar_id: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">캘린더를 선택하세요</option>
                {calendars.map((cal) => (
                  <option key={cal.id} value={cal.id}>
                    {cal.calendar_name}
                    {cal.calendar_type === "shared" && " (공유)"}
                    {cal.permission === "view" && " - 보기 전용"}
                  </option>
                ))}
              </select>
            </div>

            {/* 제목 */}
            <div className="mb-4">
              <input
                type="text"
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
                className={`w-full px-4 py-3 text-lg border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.title ? "border-red-500" : "border-gray-300"
                }`}
                placeholder="제목을 입력하세요"
              />
              {errors.title && (
                <p className="mt-1 text-sm text-red-500">{errors.title}</p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              {/* 날짜 */}
              <div>
                <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                  <Calendar className="w-4 h-4 mr-1" />
                  날짜
                </label>
                <input
                  type="date"
                  value={formData.task_date}
                  onChange={(e) =>
                    setFormData({ ...formData, task_date: e.target.value })
                  }
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.task_date ? "border-red-500" : "border-gray-300"
                  }`}
                  disabled={showRepeatConfig || !!task} // 수정 시 날짜 변경 불가(표시만)
                />
                {errors.task_date && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.task_date}
                  </p>
                )}
              </div>

              {/* 하루 종일 */}
              <div className="flex items-end">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_all_day}
                    onChange={(e) =>
                      setFormData({ ...formData, is_all_day: e.target.checked })
                    }
                    className="mr-2"
                  />
                  <span className="text-sm">하루 종일</span>
                </label>
              </div>
            </div>

            {/* 시간 (하루 종일이 아닐 때만) */}
            {!formData.is_all_day && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                    <Clock className="w-4 h-4 mr-1" />
                    시작 시간
                  </label>
                  <input
                    type="time"
                    value={formData.start_time}
                    onChange={(e) =>
                      setFormData({ ...formData, start_time: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                    <Clock className="w-4 h-4 mr-1" />
                    종료 시간
                  </label>
                  <input
                    type="time"
                    value={formData.end_time}
                    onChange={(e) =>
                      setFormData({ ...formData, end_time: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                {errors.time && (
                  <p className="col-span-2 text-sm text-red-500">
                    {errors.time}
                  </p>
                )}
              </div>
            )}

            {/* 카테고리 & 우선순위 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  카테고리
                </label>
                <select
                  value={formData.category}
                  onChange={(e) =>
                    setFormData({ ...formData, category: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {categories.map((cat) => (
                    <option key={cat.value} value={cat.value}>
                      {cat.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                  <Flag className="w-4 h-4 mr-1" />
                  우선순위
                </label>
                <select
                  value={formData.priority}
                  onChange={(e) =>
                    setFormData({ ...formData, priority: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {priorities.map((pri) => (
                    <option key={pri.value} value={pri.value}>
                      {pri.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* 위치 */}
            <div className="mb-4">
              <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                <MapPin className="w-4 h-4 mr-1" />
                위치
              </label>
              <input
                type="text"
                value={formData.location}
                onChange={(e) =>
                  setFormData({ ...formData, location: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="위치를 입력하세요"
              />
            </div>

            {/* 반복 설정 */}
            <div className="mb-4">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={showRepeatConfig}
                  onChange={(e) => setShowRepeatConfig(e.target.checked)}
                  className="mr-2"
                />
                <Repeat className="w-4 h-4 mr-1" />
                <span className="text-sm font-medium text-gray-700">
                  반복 일정
                </span>
              </label>

              {showRepeatConfig && (
                <div className="mt-3 p-4 bg-gray-50 rounded-lg">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        반복 유형
                      </label>
                      <select
                        value={formData.repeat_type || "daily"}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            repeat_type: e.target.value,
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="daily">매일</option>
                        <option value="weekly">매주</option>
                        <option value="monthly">매월</option>
                        <option value="yearly">매년</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        반복 간격
                      </label>
                      <input
                        type="number"
                        value={formData.repeat_interval || 1}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            repeat_interval: parseInt(e.target.value) || 1,
                          })
                        }
                        min="1"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        시작일
                      </label>
                      <input
                        type="date"
                        value={formData.repeat_start_date || formData.task_date}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            repeat_start_date: e.target.value,
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        종료일 (선택)
                      </label>
                      <input
                        type="date"
                        value={formData.repeat_end_date || ""}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            repeat_end_date: e.target.value,
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* 설명 */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                설명
              </label>
              <textarea
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows="4"
                placeholder="설명을 입력하세요"
              />
            </div>

            {/* 첨부파일 */}
            {onFileSelect && (
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700">
                    첨부파일
                  </label>
                  <button
                    type="button"
                    onClick={onFileSelect}
                    className="flex items-center text-sm text-blue-500 hover:text-blue-600"
                  >
                    <Paperclip className="w-4 h-4 mr-1" />
                    파일 선택
                  </button>
                </div>

                {selectedFiles && selectedFiles.length > 0 ? (
                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="text-sm text-gray-600 mb-2">
                      {selectedFiles.length}개의 파일이 선택됨
                    </div>
                    <div className="space-y-1">
                      {selectedFiles.map((file, index) => (
                        <div
                          key={file.id || index}
                          className="flex items-center text-sm text-gray-700 bg-white rounded px-2 py-1"
                        >
                          <File className="w-4 h-4 text-gray-400 mr-2" />
                          <span className="truncate flex-1">
                            {file.file_name || file.name}
                          </span>
                          <span className="text-xs text-gray-500 ml-2">
                            {file.file_size
                              ? `${(file.file_size / 1024 / 1024).toFixed(
                                  2
                                )} MB`
                              : ""}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-sm text-gray-500 italic">
                    첨부된 파일이 없습니다
                  </div>
                )}
              </div>
            )}

            {/* 버튼 */}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                취소
              </button>
              <button
                type="submit"
                className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              >
                {task ? "수정" : "저장"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
